/**
 * Login.js - 로그인 페이지
 * 이메일 기반 로그인 (OTP 인증)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Mail, LogIn } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import { checkAuthStatus, clearAuthData } from '../utils/auth';
import OTPInput from '../components/OTPInput';
import axios from 'axios';
import apiConfig from '../config/api';
import { validateData, EmailSchema, OTPSchema, OTPRequestSchema, OTPVerifySchema } from '../utils/validation';

const Login = () => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpModalMessage, setOtpModalMessage] = useState('');
  const [otpModalType, setOtpModalType] = useState('success'); // 'success' or 'error'
  const [rememberEmail, setRememberEmail] = useState(false);

  // 로그인 상태 확인 (토큰 유효성 검증 포함)
  useEffect(() => {
    // 토큰 유효성 검증을 포함한 로그인 상태 확인
    if (checkAuthStatus()) {
      // 이미 로그인되어 있고 토큰이 유효한 경우 이전 페이지 또는 대시보드로 리다이렉트
      const from = location.state?.from?.pathname || (isMobileView ? '/m/dashboard' : '/p/dashboard');
      navigate(from, { replace: true });
    } else {
      // 토큰이 만료되었거나 유효하지 않은 경우 로그인 데이터 초기화
      clearAuthData();
    }
  }, [navigate, location, isMobileView]);

  // 저장된 이메일 불러오기
  useEffect(() => {
    const savedEmail = localStorage.getItem('saved_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberEmail(true);
    }
  }, []);

  const handleRequestOTP = async () => {
    // Zod 검증 적용
    const validation = validateData(OTPRequestSchema, { email });
    if (!validation.success) {
      setError(validation.error);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      // 검증된 데이터로 OTP 요청 API 호출
      const response = await axios.post(`${apiConfig.V1}/auth/otp/request`, {
        email: validation.data.email
      });
      
      setOtpSent(true);
      // 모달창으로 성공 메시지 표시
      setOtpModalMessage(response.data.message || `${email}로 OTP가 전송되었습니다. 메일을 확인해주세요.`);
      setOtpModalType('success');
      setOtpModalOpen(true);
    } catch (error) {
      console.error('OTP 전송 실패:', error);
      const errorMessage = error.response?.data?.detail || 'OTP 전송에 실패했습니다. 다시 시도해주세요.';
      setError(errorMessage);
      // 에러도 모달창으로 표시
      setOtpModalMessage(errorMessage);
      setOtpModalType('error');
      setOtpModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!otpSent) {
      setError('먼저 OTP 코드를 요청해주세요.');
      return;
    }

    // Zod 검증 적용
    const validation = validateData(OTPVerifySchema, {
      email: email,
      otp_code: password
    });
    
    if (!validation.success) {
      setError(validation.error);
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // 검증된 데이터로 OTP 검증 및 로그인 API 호출
      const response = await axios.post(`${apiConfig.V1}/auth/otp/verify`, {
        email: validation.data.email,
        otp_code: validation.data.otp_code
      });

      const { employee_code, employee_name, email: userEmail, role, access_token, refresh_token, departments } = response.data;

      // 로그인 정보 저장
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userEmail', userEmail);
      localStorage.setItem('employeeCode', employee_code);
      localStorage.setItem('employeeName', employee_name);
      localStorage.setItem('userRole', role.toLowerCase()); // 'User', 'Admin', 'Root' -> 'user', 'admin', 'root'
      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      if (departments && departments.length > 0) {
        localStorage.setItem('departments', JSON.stringify(departments));
      }

      // ID 저장 처리
      if (rememberEmail) {
        localStorage.setItem('saved_email', email);
      } else {
        localStorage.removeItem('saved_email');
      }

      // 페이지 새로고침하여 UserRoleContext 업데이트
      // 이전 페이지 또는 대시보드로 리다이렉트
      const from = location.state?.from?.pathname || (isMobileView ? '/m/dashboard' : '/p/dashboard');
      window.location.href = from; // 전체 페이지 리로드로 Context 업데이트 보장
    } catch (error) {
      console.error('로그인 실패:', error);
      const errorMessage = error.response?.data?.detail || '로그인에 실패했습니다. OTP 코드를 확인해주세요.';
      setError(errorMessage);
      // 로그인 실패 시 OTP 입력 필드 초기화 및 첫 번째 필드에 포커스
      setPassword('');
      // OTPInput 컴포넌트가 포커스를 받을 수 있도록 약간의 지연 후 처리
      setTimeout(() => {
        const firstInput = document.querySelector('input[type="text"][maxlength="1"]');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: colors.primaryBg,
        padding: isMobileView ? '20px' : '40px',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: isMobileView ? '100%' : '420px',
          background: isDark 
            ? 'linear-gradient(140deg, rgba(32, 34, 42, 0.96) 0%, rgba(18, 19, 26, 0.88) 100%)'
            : '#FFFFFF',
          backdropFilter: isDark ? 'blur(14px)' : 'none',
          border: `1px solid ${colors.primaryBorder}`,
          borderRadius: '24px',
          boxShadow: isDark
            ? '0 28px 48px -18px rgba(0, 0, 0, 0.82)'
            : '0 12px 28px -16px rgba(15, 29, 68, 0.12)',
        }}
      >
        <CardContent sx={{ padding: isMobileView ? '24px' : '40px' }}>
          {/* 로고 및 제목 */}
          <Box sx={{ textAlign: 'center', marginBottom: '32px' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: colors.primaryText,
                marginBottom: '8px',
              }}
            >
              OnTimeLog
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.secondaryText,
              }}
            >
              프로젝트 투입시간 관리 시스템
            </Typography>
          </Box>

          {/* 에러 메시지 */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ marginBottom: '24px' }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {/* 로그인 폼 */}
          <form onSubmit={handleLogin} autoComplete="off">
            <TextField
              fullWidth
              label="이메일"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              autoComplete="off"
              inputProps={{
                autoComplete: "off",
                autoCorrect: "off",
                autoCapitalize: "off",
                spellCheck: "false"
              }}
              sx={{ 
                marginBottom: '20px',
                '& .MuiInputBase-root': {
                  backgroundColor: 'transparent !important',
                  '&:hover': {
                    backgroundColor: 'transparent !important',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'transparent !important',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'transparent !important',
                  },
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'transparent !important',
                  background: 'transparent !important',
                  '& fieldset': {
                    borderColor: `${colors.primaryBorder} !important`,
                    backgroundColor: 'transparent !important',
                  },
                  '&:hover': {
                    backgroundColor: 'transparent !important',
                    background: 'transparent !important',
                    '& fieldset': {
                      borderColor: `${colors.primaryBorder} !important`,
                      backgroundColor: 'transparent !important',
                    },
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'transparent !important',
                    background: 'transparent !important',
                    '& fieldset': {
                      borderColor: `${colors.primaryBorder} !important`,
                      borderWidth: '1px !important',
                      backgroundColor: 'transparent !important',
                    },
                    '& input': {
                      color: `${colors.primaryText} !important`,
                      backgroundColor: 'transparent !important',
                    },
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'transparent !important',
                    background: 'transparent !important',
                  },
                  '& input': {
                    color: `${colors.primaryText} !important`,
                    backgroundColor: 'transparent !important',
                    background: 'transparent !important',
                  },
                },
                '& .MuiInputBase-input': {
                  backgroundColor: 'transparent !important',
                  background: 'transparent !important',
                  textAlign: 'center',
                  transform: 'translateX(-9px)',
                  '&:focus': {
                    backgroundColor: 'transparent !important',
                    background: 'transparent !important',
                  },
                },
                '& .MuiInputAdornment-root': {
                  backgroundColor: 'transparent !important',
                },
                '& .MuiInputLabel-root': {
                  color: `${colors.secondaryText} !important`,
                  '&.Mui-focused': {
                    color: `${colors.secondaryText} !important`,
                  },
                  '&.MuiInputLabel-shrink': {
                    color: `${colors.secondaryText} !important`,
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${colors.primaryBorder} !important`,
                  backgroundColor: 'transparent !important',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${colors.primaryBorder} !important`,
                  backgroundColor: 'transparent !important',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment 
                    position="start"
                    sx={{
                      backgroundColor: 'transparent !important',
                    }}
                  >
                    <Mail size={20} color={colors.secondaryText} />
                  </InputAdornment>
                ),
              }}
            />

            {/* ID 저장 체크박스 */}
            <Box sx={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberEmail}
                    onChange={(e) => setRememberEmail(e.target.checked)}
                    disabled={isLoading}
                    sx={{
                      color: colors.secondaryText,
                      '&.Mui-checked': {
                        color: colors.accent,
                      },
                    }}
                  />
                }
                label="이메일 주소 저장"
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    color: colors.secondaryText,
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {otpSent && (
              <Box>
                <Box sx={{ marginBottom: '12px', textAlign: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.secondaryText,
                      fontSize: '14px',
                      marginBottom: '8px',
                    }}
                  >
                    OTP 코드 입력
                  </Typography>
                </Box>
                <OTPInput
                  value={password}
                  onChange={setPassword}
                  length={6}
                  disabled={isLoading}
                  error={!!error}
                />
              </Box>
            )}

            {/* OTP 요청 버튼 (OTP 미전송 시) */}
            {!otpSent && (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleRequestOTP}
                disabled={isLoading || !email}
                sx={{
                  marginBottom: '16px',
                  padding: '12px',
                  borderColor: colors.primaryBorder,
                  color: colors.primaryText,
                  backgroundColor: 'transparent !important',
                  background: 'transparent !important',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: '#2196F3',
                    backgroundColor: isDark ? 'rgba(33, 150, 243, 0.15) !important' : 'rgba(33, 150, 243, 0.1) !important',
                    background: isDark ? 'rgba(33, 150, 243, 0.15) !important' : 'rgba(33, 150, 243, 0.1) !important',
                  },
                  '&:active': {
                    borderColor: '#2196F3',
                    backgroundColor: isDark ? 'rgba(33, 150, 243, 0.2) !important' : 'rgba(33, 150, 243, 0.15) !important',
                    background: isDark ? 'rgba(33, 150, 243, 0.2) !important' : 'rgba(33, 150, 243, 0.15) !important',
                  },
                  '&:focus': {
                    borderColor: '#2196F3',
                    backgroundColor: isDark ? 'rgba(33, 150, 243, 0.15) !important' : 'rgba(33, 150, 243, 0.1) !important',
                    background: isDark ? 'rgba(33, 150, 243, 0.15) !important' : 'rgba(33, 150, 243, 0.1) !important',
                  },
                  '& .MuiTouchRipple-root': {
                    display: 'none',
                  },
                  '& .MuiButton-root': {
                    backgroundColor: 'transparent !important',
                    background: 'transparent !important',
                  },
                  '&::before': {
                    display: 'none',
                  },
                  '&::after': {
                    display: 'none',
                  },
                }}
              >
                {isLoading ? 'OTP 전송 중...' : 'OTP 요청'}
              </Button>
            )}

            {/* 로그인 버튼 및 재요청 버튼 (OTP 발송 후에만 표시) */}
            {otpSent && (
              <>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={isLoading || !email || !password}
                  sx={{
                    marginBottom: '12px',
                    padding: '14px',
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '16px',
                    '&:hover': {
                      backgroundColor: colors.accentHover,
                    },
                    '&:disabled': {
                      backgroundColor: colors.primaryBorder,
                      color: colors.secondaryText,
                    },
                  }}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LogIn size={20} />}
                >
                  {isLoading ? '로그인 중...' : '로그인'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRequestOTP}
                  disabled={isLoading || !email}
                  sx={{
                    padding: '12px',
                    borderColor: colors.primaryBorder,
                    color: colors.primaryText,
                    backgroundColor: 'transparent !important',
                    background: 'transparent !important',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: '#2196F3',
                      backgroundColor: isDark ? 'rgba(33, 150, 243, 0.15) !important' : 'rgba(33, 150, 243, 0.1) !important',
                      background: isDark ? 'rgba(33, 150, 243, 0.15) !important' : 'rgba(33, 150, 243, 0.1) !important',
                    },
                    '&:active': {
                      borderColor: '#2196F3',
                      backgroundColor: isDark ? 'rgba(33, 150, 243, 0.2) !important' : 'rgba(33, 150, 243, 0.15) !important',
                      background: isDark ? 'rgba(33, 150, 243, 0.2) !important' : 'rgba(33, 150, 243, 0.15) !important',
                    },
                    '&:focus': {
                      borderColor: '#2196F3',
                      backgroundColor: isDark ? 'rgba(33, 150, 243, 0.15) !important' : 'rgba(33, 150, 243, 0.1) !important',
                      background: isDark ? 'rgba(33, 150, 243, 0.15) !important' : 'rgba(33, 150, 243, 0.1) !important',
                    },
                    '& .MuiTouchRipple-root': {
                      display: 'none',
                    },
                    '& .MuiButton-root': {
                      backgroundColor: 'transparent !important',
                      background: 'transparent !important',
                    },
                    '&::before': {
                      display: 'none',
                    },
                    '&::after': {
                      display: 'none',
                    },
                  }}
                >
                  {isLoading ? 'OTP 전송 중...' : 'OTP 재요청'}
                </Button>
              </>
            )}
          </form>

          {/* 안내 메시지 */}
          <Box sx={{ marginTop: '24px', textAlign: 'center' }}>
            {otpSent ? (
              <Typography
                variant="caption"
                sx={{
                  color: colors.secondaryText,
                  fontSize: '12px',
                }}
              >
                이메일로 받은 OTP 코드를 입력하세요. (10분간 유효)
              </Typography>
            ) : (
              <Typography
                variant="caption"
                sx={{
                  color: colors.secondaryText,
                  fontSize: '12px',
                }}
              >
                등록된 이메일 주소로 OTP 코드를 요청하세요.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* OTP 발송 알림 모달 */}
      <Dialog
        open={otpModalOpen}
        onClose={() => setOtpModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: isDark ? colors.background : '#fff',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {otpModalType === 'success' ? (
            <>
              <Mail size={20} style={{ color: colors.accent }} />
              <Typography variant="h6" component="span">OTP 발송 완료</Typography>
            </>
          ) : (
            <>
              <Alert severity="error" sx={{ p: 0, mr: 1 }} />
              <Typography variant="h6" component="span">OTP 발송 실패</Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: colors.primaryText, mt: 1 }}>
            {otpModalMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOtpModalOpen(false)}
            variant="contained"
            sx={{ backgroundColor: colors.accent }}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;

